import { parseCoordinateValue } from '@gmn-portal/backend';
import { Copy, MapPin } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { toast } from 'sonner';
import { useIntl } from '@/core/i18n/use-intl';
import { useCoordinateFormat } from '@/shared/hooks/use-coordinate-format';
import {
  formatCoordinateForCopy,
  getCoordinatePlaceholder,
  toEditableCoordinate,
  tryParseCoordinatePair,
} from '@/shared/utils';
import { Button } from '../../button';
import { Card, CardContent } from '../../card';
import { FormItem, FormMessage } from '../../form';
import { Input } from '../../input';
import { Label } from '../../label';
import AutoFormLabel from '../common/label';
import type { AutoFormInputComponentProps } from '../types';

type EditableCoordinate = {
  latitude: string;
  longitude: string;
};

const toStoredCoordinate = (value: EditableCoordinate) => {
  const latitude = parseCoordinateValue(value.latitude, 'latitude');
  const longitude = parseCoordinateValue(value.longitude, 'longitude');

  return {
    ...(latitude !== null ? { latitude } : {}),
    ...(longitude !== null ? { longitude } : {}),
  };
};

export default function AutoFormCoordinate({
  label,
  isRequired,
  fieldConfigItem,
  field,
  fieldProps,
}: AutoFormInputComponentProps & { field: ControllerRenderProps<FieldValues, string> }) {
  const { t } = useIntl();
  const coordinateFormat = useCoordinateFormat();
  const { showLabel: _showLabel, disabled, ...restFieldProps } = fieldProps;
  const showLabel = _showLabel === undefined ? true : _showLabel;
  const inputRefs = useRef<{
    latitude?: HTMLInputElement | null;
    longitude?: HTMLInputElement | null;
  }>({});
  const fieldValue = field.value as Record<string, unknown> | undefined;
  const [value, setValue] = useState<EditableCoordinate>(() =>
    toEditableCoordinate(fieldValue, coordinateFormat)
  );
  const ownUpdateRef = useRef(false);

  useEffect(() => {
    if (ownUpdateRef.current) {
      ownUpdateRef.current = false;
      return;
    }

    const nextValue = toEditableCoordinate(fieldValue, coordinateFormat);
    setValue(nextValue);
    if (inputRefs.current.latitude && document.activeElement !== inputRefs.current.latitude) {
      inputRefs.current.latitude.value = nextValue.latitude;
    }
    if (inputRefs.current.longitude && document.activeElement !== inputRefs.current.longitude) {
      inputRefs.current.longitude.value = nextValue.longitude;
    }
  }, [coordinateFormat, fieldValue]);

  const updateValue = useCallback(
    (updates: Partial<EditableCoordinate>) => {
      setValue((previous) => {
        const next = { ...previous, ...updates };
        ownUpdateRef.current = true;
        field.onChange(toStoredCoordinate(next));
        return next;
      });
    },
    [field]
  );

  const canCopy = useMemo(() => {
    return (
      parseCoordinateValue(value.latitude, 'latitude') !== null &&
      parseCoordinateValue(value.longitude, 'longitude') !== null
    );
  }, [value.latitude, value.longitude]);

  const handleCopy = useCallback(async () => {
    const latitude = parseCoordinateValue(value.latitude, 'latitude');
    const longitude = parseCoordinateValue(value.longitude, 'longitude');
    if (latitude === null || longitude === null) return;

    try {
      await navigator.clipboard.writeText(
        formatCoordinateForCopy({ latitude, longitude }, coordinateFormat)
      );
      toast.success(t('metadata.positions.copySuccess'));
    } catch {
      toast.error(t('metadata.positions.copyError'));
    }
  }, [coordinateFormat, t, value.latitude, value.longitude]);

  const handlePaste = useCallback(
    (target: 'latitude' | 'longitude') => (e: React.ClipboardEvent<HTMLInputElement>) => {
      const parsed = tryParseCoordinatePair(e.clipboardData.getData('text'));
      if (!parsed) return;
      e.preventDefault();
      const next = { latitude: parsed[0], longitude: parsed[1] };
      (e.target as HTMLInputElement).value = target === 'latitude' ? parsed[0] : parsed[1];
      if (target === 'latitude' && inputRefs.current.longitude) {
        inputRefs.current.longitude.value = parsed[1];
      }
      if (target === 'longitude' && inputRefs.current.latitude) {
        inputRefs.current.latitude.value = parsed[0];
      }
      updateValue(next);
    },
    [updateValue]
  );

  return (
    <FormItem className="col-span-6" {...restFieldProps}>
      <Card className="border-border/60" data-testid="coordinate-field">
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-3 mb-2">
            {showLabel && (
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
                  <MapPin className="size-4 text-primary" />
                </div>
                <AutoFormLabel
                  label={fieldConfigItem?.label || label}
                  isRequired={isRequired}
                  className="text-sm font-semibold"
                />
              </div>
            )}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleCopy}
              disabled={disabled || !canCopy}
              data-testid="coordinate-copy-button"
              className="h-8"
            >
              <Copy className="size-3.5 mr-1.5" />
              {t('metadata.positions.copy')}
            </Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-[1fr_1fr]">
            <div className="space-y-1">
              <Label className="text-xs" htmlFor={`${field.name}-latitude`}>
                {t('metadata.positions.latitude')}
              </Label>
              <Input
                id={`${field.name}-latitude`}
                data-testid="coordinate-latitude"
                defaultValue={value.latitude}
                ref={(node) => {
                  inputRefs.current.latitude = node;
                }}
                onChange={(e) => updateValue({ latitude: e.target.value })}
                onPaste={handlePaste('latitude')}
                placeholder={getCoordinatePlaceholder('latitude', coordinateFormat)}
                disabled={disabled}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor={`${field.name}-longitude`}>
                {t('metadata.positions.longitude')}
              </Label>
              <Input
                id={`${field.name}-longitude`}
                data-testid="coordinate-longitude"
                defaultValue={value.longitude}
                ref={(node) => {
                  inputRefs.current.longitude = node;
                }}
                onChange={(e) => updateValue({ longitude: e.target.value })}
                onPaste={handlePaste('longitude')}
                placeholder={getCoordinatePlaceholder('longitude', coordinateFormat)}
                disabled={disabled}
              />
            </div>
          </div>
          <FormMessage />
        </CardContent>
      </Card>
    </FormItem>
  );
}
